const WARSAW_TIME_ZONE = 'Europe/Warsaw'

// Module-level formatter: `Intl.DateTimeFormat` construction dominates the
// cost of every call, and the dashboard calendar keys each event, column and
// pill through this helper on every render.
const warsawDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: WARSAW_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
})

/** Civil calendar day `YYYY-MM-DD` for `date` in Europe/Warsaw. */
export function getWarsawCalendarDay(date: Date): string {
  return warsawDayFormatter.format(date)
}

function warsawCalendarDayToUtcMidnight(day: string): number {
  const [year, month, dayOfMonth] = day.split('-').map(Number)
  return Date.UTC(year, month - 1, dayOfMonth)
}

/**
 * Civil calendar day `days` after `asOf` in Europe/Warsaw (`YYYY-MM-DD`).
 * `days` may be negative. Month, year and DST boundaries are handled by UTC
 * calendar arithmetic, so the offset never lands on a shifted local hour.
 */
export function getWarsawCalendarDayPlusDays(asOf: Date, days: number): string {
  const shiftedUtc = new Date(
    warsawCalendarDayToUtcMidnight(getWarsawCalendarDay(asOf)) + days * 86_400_000
  )
  return shiftedUtc.toISOString().slice(0, 10)
}

/** Next civil calendar day after `asOf` in Europe/Warsaw (`YYYY-MM-DD`). */
export function getTomorrowWarsawCalendarDay(asOf: Date): string {
  return getWarsawCalendarDayPlusDays(asOf, 1)
}

const warsawTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: WARSAW_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  // `hour12: false` yields hour 24 for midnight on some ICU versions; h23 does not.
  hourCycle: 'h23'
})

const warsawLongDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: WARSAW_TIME_ZONE,
  day: 'numeric',
  month: 'long',
  year: 'numeric'
})

/**
 * Wall clock `HH:mm` for `date` in Europe/Warsaw.
 *
 * Lessons and meetings are scheduled in the school's own time, so the hour a
 * student is told must not depend on who renders it. `date-fns` `format` reads
 * the renderer's zone, which makes a server-rendered page (containers run UTC)
 * disagree with the same page rendered in the browser.
 */
export function formatWarsawTime(date: Date): string {
  return warsawTimeFormatter.format(date)
}

/** Long-form Polish date in Europe/Warsaw, e.g. `17 sierpnia 2026`. */
export function formatWarsawLongDate(date: Date): string {
  return warsawLongDateFormatter.format(date)
}

/** Minutes since Warsaw midnight - the calendar grid's vertical coordinate. */
export function getWarsawMinutesSinceMidnight(date: Date): number {
  const [hours, minutes] = formatWarsawTime(date).split(':').map(Number)
  return hours * 60 + minutes
}

const warsawShortDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: WARSAW_TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric'
})

const warsawNumericDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: WARSAW_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
})

/** Short Polish date-time in Europe/Warsaw, e.g. `17 sie 2026 13:00`. */
export function formatWarsawShortDateTime(date: Date): string {
  return `${warsawShortDateFormatter.format(date)} ${formatWarsawTime(date)}`
}

const warsawDayMonthFormatter = new Intl.DateTimeFormat('pl-PL', {
  timeZone: WARSAW_TIME_ZONE,
  day: 'numeric',
  month: 'short'
})

/** Short Polish day and month in Europe/Warsaw, e.g. `7 sie`. */
export function formatWarsawDayMonth(date: Date): string {
  return warsawDayMonthFormatter.format(date)
}

/** Numeric Polish date in Europe/Warsaw, e.g. `17.08.2026`. */
export function formatWarsawNumericDate(date: Date): string {
  return warsawNumericDateFormatter.format(date)
}

/** Do both instants fall on the same civil calendar day in Europe/Warsaw? */
export function isSameWarsawCalendarDay(left: Date, right: Date): boolean {
  return getWarsawCalendarDay(left) === getWarsawCalendarDay(right)
}

/**
 * Whole civil days between `from` and `to` in Europe/Warsaw. Positive when
 * `to` is on a later Warsaw day. Counts calendar-day boundaries, not 24-hour
 * spans - 23:59 to 00:01 is one day.
 */
export function warsawCalendarDaysBetween(from: Date, to: Date): number {
  const fromMidnight = warsawCalendarDayToUtcMidnight(getWarsawCalendarDay(from))
  const toMidnight = warsawCalendarDayToUtcMidnight(getWarsawCalendarDay(to))
  return Math.round((toMidnight - fromMidnight) / 86_400_000)
}
